import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  fetchOptionsAccount,
  fetchOptionsCapitalPeriods,
  fetchOptionsCurrentCapitalPeriods,
  fetchOptionsDashboardCapitalPeriods,
  fetchOptionsDashboardCurrentCapitalPeriods,
  fetchOptionsDashboardPerformance,
  fetchOptionsEquityHistory,
  fetchOptionsPerformance,
  setOptionsDemoMode,
  syncOptionsAccount,
} from '../lib/optionsApi';
import { buildCustomRangePayload, getDefaultCustomRange } from '../components/options/optionsDashboardUtils';
import { useOptionsMode } from '../context/OptionsModeContext';
import { useOptionsLocale } from '../context/OptionsLanguageContext';
import { emitOptionsOpenOperationsUpdated } from '../lib/optionsEvents';
import { useOptionsEntitlements } from '../context/OptionsEntitlementsContext';

// Single short retry for transient bootstrap timing (e.g. right after a first Deribit
// connect + initial sync). Deliberately bounded - never loops, never masks a real,
// persistent failure.
const fetchWithRetry = async (fn, retries = 1, delayMs = 1200) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return fetchWithRetry(fn, retries - 1, delayMs);
  }
};

export const useOptionsDashboardData = () => {
  const { mode } = useOptionsMode();
  const { t } = useOptionsLocale();
  const { isPro, loading: entitlementsLoading } = useOptionsEntitlements();
  const [accountData, setAccountData] = useState(null);
  const [currentCapitalPeriodsData, setCurrentCapitalPeriodsData] = useState(null);
  const [capitalPeriodsData, setCapitalPeriodsData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  // `loading` gates the FULL-PAGE loader (account connect state, mount / demo<->real switch only).
  const [loading, setLoading] = useState(true);
  const [loadedMode, setLoadedMode] = useState(null);
  // Set only when the CRITICAL connection-status fetch fails persistently (after retry).
  // A real, persistent error - distinct from "not connected yet" and from "loading".
  const [criticalError, setCriticalError] = useState(false);
  // `syncing` is for a REAL Deribit account sync (manual "Sync Now").
  const [syncing, setSyncing] = useState(false);
  // `rangeFetching` is LOCAL to the Trading Performance range filter (30D/90D/YTD/ALL/Custom).
  // It must never gate the full dashboard - the page stays mounted, previous data stays visible.
  const [rangeFetching, setRangeFetching] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [selectedRange, setSelectedRange] = useState('30d');
  const [customRange, setCustomRange] = useState(getDefaultCustomRange());
  const [appliedCustomRange, setAppliedCustomRange] = useState(getDefaultCustomRange());
  const rangeRequestIdRef = useRef(0);
  const initialPayloadRef = useRef(null);
  const mountRequestIdRef = useRef(0);

  const requestPayload = useMemo(() => {
    if (selectedRange !== 'custom') {
      return { range: selectedRange, start: null, end: null };
    }
    const { start, end } = buildCustomRangePayload(appliedCustomRange);
    return { range: selectedRange, start, end };
  }, [selectedRange, appliedCustomRange]);

  const loadCurrentAccount = useCallback(async () => {
    const [account, currentCapitalPeriods] = await Promise.all([
      fetchOptionsAccount(),
      isPro ? fetchOptionsCurrentCapitalPeriods() : fetchOptionsDashboardCurrentCapitalPeriods(),
    ]);
    setAccountData(account);
    setCurrentCapitalPeriodsData(currentCapitalPeriods);
    return account;
  }, [isPro]);

  // Reads already-synced/persisted performance data for a given range. This NEVER
  // triggers a Deribit account sync - it only queries /performance, /equity-history
  // and /capital-periods with the requested range/start/end. Each call is retried once
  // and evaluated independently (Promise.allSettled): a single slow/failing endpoint
  // never blocks the other two, and a missing piece simply keeps its "no data yet"
  // card-level empty state instead of failing the whole dashboard.
  const loadHistoricalData = useCallback(async (payload) => {
    const [performanceResult, historyResult, capitalPeriodsResult] = await Promise.allSettled([
      fetchWithRetry(() => isPro ? fetchOptionsPerformance(payload) : fetchOptionsDashboardPerformance(payload)),
      isPro ? fetchWithRetry(() => fetchOptionsEquityHistory({ ...payload, limit: 500 })) : Promise.resolve(null),
      fetchWithRetry(() => isPro ? fetchOptionsCapitalPeriods(payload) : fetchOptionsDashboardCapitalPeriods(payload)),
    ]);
    return {
      performance: performanceResult.status === 'fulfilled' ? performanceResult.value : null,
      history: historyResult.status === 'fulfilled' ? historyResult.value : null,
      capitalPeriods: capitalPeriodsResult.status === 'fulfilled' ? capitalPeriodsResult.value : null,
      hadFailure: [performanceResult, historyResult, capitalPeriodsResult].some((result) => result.status === 'rejected'),
    };
  }, [isPro]);

  // Only overwrite a slice of state when the fresh fetch actually succeeded - keeps
  // previously loaded values visible instead of blanking cards on a transient failure.
  const applyHistoricalData = useCallback((historical) => {
    if (historical.performance !== null) setPerformanceData(historical.performance);
    if (historical.history !== null) setHistoryData(historical.history);
    if (historical.capitalPeriods !== null) setCapitalPeriodsData(historical.capitalPeriods);
  }, []);

  const loadAll = useCallback(async (payload) => {
    // Synchronous in-flight guard: React.StrictMode double-invokes this mount effect
    // in development (both invocations run back-to-back before any await settles),
    // which previously fired every request twice. Bail out immediately on the
    // second, concurrent call instead of duplicating network calls.
    if (mountRequestIdRef.current) {
      return;
    }
    mountRequestIdRef.current = 1;
    setLoading(true);
    setLoadedMode(null);
    setCriticalError(false);
    // Reset optional data on a fresh mount/mode switch so values from a previous mode
    // (e.g. demo) never linger under the new mode while its data is still loading.
    setPerformanceData(null);
    setHistoryData(null);
    setCapitalPeriodsData(null);
    setCurrentCapitalPeriodsData(null);
    try {
      try {
        // CRITICAL: connection status. Retried once so a transient blip right after a
        // brand-new Deribit connect + initial sync never falsely reads as "not connected".
        const account = await fetchWithRetry(() => fetchOptionsAccount());
        setAccountData(account);
      } catch (error) {
        setCriticalError(true);
        setLoadedMode(mode);
        setLoading(false);
        return;
      }

      const [currentPeriodsResult, historicalResult] = await Promise.allSettled([
        fetchWithRetry(() => isPro ? fetchOptionsCurrentCapitalPeriods() : fetchOptionsDashboardCurrentCapitalPeriods()),
        loadHistoricalData(payload),
      ]);
      if (currentPeriodsResult.status === 'fulfilled') {
        setCurrentCapitalPeriodsData(currentPeriodsResult.value);
      }
      let hadOptionalFailure = currentPeriodsResult.status === 'rejected';
      if (historicalResult.status === 'fulfilled') {
        applyHistoricalData(historicalResult.value);
        hadOptionalFailure = hadOptionalFailure || historicalResult.value.hadFailure;
      } else {
        hadOptionalFailure = true;
      }
      if (hadOptionalFailure) {
        toast.info(t('toasts.partialDashboardData'), { id: 'options-partial-dashboard-data' });
      }
      setLoadedMode(mode);
      setLoading(false);
    } finally {
      mountRequestIdRef.current = 0;
    }
  }, [applyHistoricalData, isPro, loadHistoricalData, mode, t]);

  // Mount / demo<->real switch ONLY. Intentionally does not depend on selectedRange
  // so that changing the Trading Performance range never remounts/reloads the page.
  // `initialPayloadRef` marks which payload this mount/mode-switch already fetched,
  // so the range effect below can safely skip its own first run even under
  // React.StrictMode's dev-only double-invoke (which would otherwise defeat a plain
  // boolean "first run" flag and cause duplicate requests).
  useEffect(() => {
    if (entitlementsLoading) return;
    setOptionsDemoMode(mode === 'demo');
    initialPayloadRef.current = requestPayload;
    loadAll(requestPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, entitlementsLoading, isPro]);

  // Range/custom-range change ONLY. Local loading state, keeps previous data visible,
  // never touches the full-page `loading` state. Skips the payload already fetched by
  // the mount effect above (referential equality with the memoized requestPayload).
  useEffect(() => {
    if (entitlementsLoading || requestPayload === initialPayloadRef.current) {
      return;
    }
    const requestId = rangeRequestIdRef.current + 1;
    rangeRequestIdRef.current = requestId;
    setRangeFetching(true);
    loadHistoricalData(requestPayload)
      .then((historical) => {
        if (rangeRequestIdRef.current === requestId) {
          applyHistoricalData(historical);
          if (historical.hadFailure) {
            toast.info(t('toasts.partialDashboardData'), { id: 'options-partial-dashboard-data' });
          }
        }
      })
      .finally(() => {
        if (rangeRequestIdRef.current === requestId) {
          setRangeFetching(false);
        }
      });
  }, [entitlementsLoading, requestPayload, loadHistoricalData, applyHistoricalData, t]);

  const handleApplyCustomRange = useCallback(() => {
    if (!customRange.startDate || !customRange.endDate) {
      toast.error(t('toasts.selectBothDates'));
      return;
    }
    if (customRange.startDate > customRange.endDate) {
      toast.error(t('toasts.startBeforeEnd'));
      return;
    }
    setSelectedRange('custom');
    setAppliedCustomRange(customRange);
  }, [customRange, t]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await syncOptionsAccount();
      toast.success(t('toasts.syncCompleted'));
      const [, historical] = await Promise.all([loadCurrentAccount(), loadHistoricalData(requestPayload)]);
      applyHistoricalData(historical);
      emitOptionsOpenOperationsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.syncFailed'));
    } finally {
      setSyncing(false);
    }
  }, [loadCurrentAccount, loadHistoricalData, applyHistoricalData, requestPayload, t]);

  return {
    accountData,
    performanceData,
    currentCapitalPeriodsData,
    capitalPeriodsData,
    historyData,
    loading,
    loadedMode,
    criticalError,
    syncing,
    rangeFetching,
    selectedCurrency,
    setSelectedCurrency,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    handleApplyCustomRange,
    handleSync,
    reloadDashboard: () => loadAll(requestPayload),
  };
};

export default useOptionsDashboardData;